/**
 * SnipModal — region-select crop over a captured video frame.
 *
 * VideoWatch captures the paused frame to a data URL (canvas grab); this modal
 * shows it frozen and lets the student drag a rectangle over the part they're
 * asking about (a sub-question on the board, one diagram on a busy slide).
 * Confirm crops via canvas and hands the cropped data URL back to the chat
 * panel as a pending attachment. "Use full frame" skips selection.
 *
 * Portal to document.body — same escape-the-panel-transforms reason as the
 * VidyaSvg modal.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ScissorsIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface SnipModalProps {
  /** Captured frame as a data URL. */
  imageUrl: string;
  /** Called with the cropped (or full) frame data URL. */
  onConfirm: (dataUrl: string) => void;
  onClose: () => void;
}

interface Rect { x: number; y: number; w: number; h: number }

export const SnipModal: React.FC<SnipModalProps> = ({ imageUrl, onConfirm, onClose }) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [drag, setDrag] = useState<{ sx: number; sy: number } | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Pointer coords relative to the displayed image, clamped to its box.
  const toLocal = (e: React.PointerEvent) => {
    const box = imgRef.current!.getBoundingClientRect();
    return {
      x: Math.min(Math.max(e.clientX - box.left, 0), box.width),
      y: Math.min(Math.max(e.clientY - box.top, 0), box.height),
    };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!imgRef.current) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const p = toLocal(e);
    setDrag({ sx: p.x, sy: p.y });
    setRect({ x: p.x, y: p.y, w: 0, h: 0 });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag || !imgRef.current) return;
    const p = toLocal(e);
    setRect({
      x: Math.min(drag.sx, p.x),
      y: Math.min(drag.sy, p.y),
      w: Math.abs(p.x - drag.sx),
      h: Math.abs(p.y - drag.sy),
    });
  };

  const onPointerUp = () => setDrag(null);

  const crop = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    // No / degenerate selection → full frame.
    if (!rect || rect.w < 8 || rect.h < 8) { onConfirm(imageUrl); return; }
    const scaleX = img.naturalWidth / img.clientWidth;
    const scaleY = img.naturalHeight / img.clientHeight;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(rect.w * scaleX));
    canvas.height = Math.max(1, Math.round(rect.h * scaleY));
    const ctx = canvas.getContext('2d');
    if (!ctx) { onConfirm(imageUrl); return; }
    ctx.drawImage(
      img,
      rect.x * scaleX, rect.y * scaleY, rect.w * scaleX, rect.h * scaleY,
      0, 0, canvas.width, canvas.height,
    );
    onConfirm(canvas.toDataURL('image/jpeg', 0.92));
  }, [rect, imageUrl, onConfirm]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-3 bg-slate-900/80 p-6 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="text-sm font-medium text-white/90" onClick={(e) => e.stopPropagation()}>
        Drag to select the part you're asking about — or use the full frame
      </div>
      <div
        // overflow-hidden clips the selection's outside-dim (the 9999px box
        // shadow) to the image bounds
        className="relative max-h-[75vh] max-w-[90vw] cursor-crosshair touch-none select-none overflow-hidden rounded-lg"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <img
          ref={imgRef}
          src={imageUrl}
          alt="Paused video frame"
          draggable={false}
          className="max-h-[75vh] max-w-[90vw] rounded-lg shadow-2xl"
        />
        {rect && rect.w > 2 && rect.h > 2 && (
          <div
            className="pointer-events-none absolute border-2 border-cyan-400"
            style={{
              left: rect.x, top: rect.y, width: rect.w, height: rect.h,
              // dim everything OUTSIDE the selection
              boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
            }}
          />
        )}
      </div>
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={crop}
          disabled={!rect || rect.w < 8 || rect.h < 8}
          className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white shadow transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg,#06B6D4,#3B82F6)' }}
        >
          <ScissorsIcon className="h-4 w-4" /> Snip selection
        </button>
        <button
          onClick={() => onConfirm(imageUrl)}
          className="rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/20"
        >
          Use full frame
        </button>
        <button
          onClick={onClose}
          aria-label="Cancel snip"
          className="rounded-lg p-2 text-white/70 transition hover:bg-white/10 hover:text-white"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default SnipModal;
